/* eslint-disable @repo/internal/deprecations/deprecation-ticket-required */
import { useContext, useMemo } from 'react';

import type { Features } from '../../../../common/constants/bootstrap';
import type { PrimaryProductKey } from '../../../../common/constants/products';
import type { BootstrapVariables } from '../../../../common/constants/relay/types';
import type { UserDetailsWithStatus } from '../../../../common/constants/types';
import { getUser } from '../../../../common/utils/bootstrap';
import {
	useBootstrapStateStore,
	useCloudIdStore,
	useFeaturesStore,
	useOrgIdStore,
	usePrimaryProductStore,
	useUserStore,
} from '../../bootstrap';
import { DynamicBootstrapContext, StaticBootstrapContext } from '../bootstrap-context-provider';
import { serveBootstrapProviderExp } from '../utils';

type HookReturn<T> = T[];

/**
 * OLD PLAN. DO NOT MIGRATE NEW ACCESSORS TO THIS FILE
 * See ../bootstrap-context-provider/accessors/index.tsx for the new migration plan and implementation
 */

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-value accessors
 *
 * e.g. const [{ useCloudId, useQueryParams }] = useBootstrap()
 * -> const [cloudId] = useCloudId();
 * -> const [queryParams] = useQueryParams();
 */
const useBootstrapStateContext = (cb: (state: BootstrapVariables) => BootstrapVariables) => {
	const staticContext = useContext(StaticBootstrapContext);
	const dynamicContext = useContext(DynamicBootstrapContext);
	const context = useMemo(() => {
		return cb({
			...staticContext,
			searchSessionId: dynamicContext?.searchSessionId,
			referrer: dynamicContext?.referrer,
			thirdPartyConfigs: dynamicContext?.thirdPartyConfigs,
		});
	}, [
		staticContext,
		dynamicContext.searchSessionId,
		dynamicContext.referrer,
		dynamicContext.thirdPartyConfigs,
		cb,
	]);

	return context;
};

/**
 * @deprecated
 * Prefer accessing values from the store directly with their individual helpers
 *
 * Retrieve BootstrapVariables (with applied callback) from the store. Should only be used at the toplevel of relay as this combines statid and dynamic props and may rerender.
 *
 * @deprecated Use individual accessor hooks instead (e.g. useCloudId, useThirdPartyConfigs, useAvailableProducts, etc.)
 * This monolithic hook causes unnecessary re-renders. See QS-9283.
 */
export const useBootstrapState: (
	cb: (state: BootstrapVariables) => BootstrapVariables,
) => BootstrapVariables = (cb: (state: BootstrapVariables) => BootstrapVariables) => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useBootstrapStateContext(cb) : useBootstrapStateStore(cb);
};

/**
 * Retrieve features from the store. Features are defined by the host product and allow fg/experiment service outside our package.
 */
export const useFeatures: () => HookReturn<Features | undefined> = (): HookReturn<
	Features | undefined
> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = useFeaturesStore();

	return serveContext
		? [staticContext.features]
		: ([fromStore] satisfies HookReturn<Features | undefined>); // HookReturnValue from RSS satisfies this, but we ignore the rest of it
};

/**
 * Retrieve cloudId from the store. CloudId identifies the Atlassian cloud instance.
 */
export const useCloudId: () => HookReturn<string | undefined> = (): HookReturn<
	string | undefined
> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = useCloudIdStore();

	return serveContext
		? [staticContext.cloudId]
		: ([fromStore] satisfies HookReturn<string | undefined>);
};

/**
 * Retrieve orgId from the store. OrgId identifies the Atlassian organization.
 */
export const useOrgId: () => HookReturn<string | undefined> = (): HookReturn<
	string | undefined
> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = useOrgIdStore();

	return serveContext
		? [staticContext.orgId]
		: ([fromStore] satisfies HookReturn<string | undefined>);
};

/**
 * Retrieve primaryProduct from the store. PrimaryProduct identifies the main product context.
 */
export const usePrimaryProduct: () => HookReturn<PrimaryProductKey | undefined> = (): HookReturn<
	PrimaryProductKey | undefined
> => {
	const serveContext = serveBootstrapProviderExp();
	const staticContext = useContext(StaticBootstrapContext);
	const [fromStore] = usePrimaryProductStore();

	return serveContext
		? [staticContext.primaryProduct]
		: ([fromStore] satisfies HookReturn<PrimaryProductKey | undefined>);
};

/**
 * Retrieve user details from the store with authentication status.
 */
export const useUser: () => HookReturn<UserDetailsWithStatus> =
	(): HookReturn<UserDetailsWithStatus> => {
		const serveContext = serveBootstrapProviderExp();
		const staticContext = useContext(StaticBootstrapContext);
		const [fromStore] = useUserStore();

		// Apply getUser transformation for context path (store already applies it in selector)
		return serveContext
			? [getUser(staticContext.user)]
			: ([fromStore] satisfies HookReturn<UserDetailsWithStatus>);
	};
